import { FCElement } from '@fc/component';
import { FCEvents, MediaPlayEvent } from '@fc/events';
import { DialogCloseEvent } from '@fc/dialog';

// Send play/pause command to an embedded iframe (YouTube or Vimeo)
const postIframeCmd = (iframe, action) => {
  const type = iframe?.dataset?.videoType;
  const msg = type === 'youtube' ? `{"event":"command","func":"${action}Video","args":""}` : `{"method":"${action}"}`;
  const origin = type === 'youtube' ? 'https://www.youtube.com' : 'https://player.vimeo.com';
  iframe?.contentWindow?.postMessage(msg, origin);
};

const isElementWithinActiveRoot = (element, activeRoot) => activeRoot instanceof Element && activeRoot.contains(element);

const pauseMediaNode = (node) => {
  if (!(node instanceof Element)) return;

  if (typeof node.pauseMedia === 'function') {
    node.pauseMedia();
    return;
  }

  if (node instanceof HTMLVideoElement) {
    node.pause();
    return;
  }

  if (node instanceof HTMLIFrameElement && node.dataset.videoType) {
    postIframeCmd(node, 'pause');
    return;
  }

  if (node.tagName?.toLowerCase() === 'model-viewer' && typeof node.pause === 'function') {
    node.pause();
  }
};

const INLINE_VIDEO_ROOT_SELECTOR = '[data-fit-inline-video-toggle]';

const markVideoFallbackImagesDecorative = (video) => {
  if (!(video instanceof HTMLVideoElement)) return;

  video.querySelectorAll('img:not([alt])').forEach((image) => {
    image.setAttribute('alt', '');
  });
};

const updateInlineVideoState = (video, root) => {
  if (!(video instanceof HTMLVideoElement) || !(root instanceof HTMLElement)) return;

  const playing = !video.paused && !video.ended;
  const playLabel = root.dataset.fitPlayLabel || '';
  const pauseLabel = root.dataset.fitPauseLabel || '';

  root.setAttribute('aria-pressed', String(playing));
  root.setAttribute('aria-label', playing ? pauseLabel : playLabel);
};

const toggleInlineVideo = (video) => {
  if (!(video instanceof HTMLVideoElement)) return;

  if (video.paused || video.ended) {
    video.play()?.catch(() => {});
    return;
  }

  video.pause();
};

const getInlineVideoForToggle = (root) => {
  if (!(root instanceof HTMLElement)) return null;

  const nestedVideo = root.querySelector('video');
  if (nestedVideo instanceof HTMLVideoElement) return nestedVideo;

  const shell = root.closest('[data-fit-inline-video-shell]');
  const shellVideo = shell?.querySelector('video');
  return shellVideo instanceof HTMLVideoElement ? shellVideo : null;
};

const initInlineVideoToggle = (root) => {
  if (!(root instanceof HTMLElement) || root.hasAttribute('data-fit-inline-video-ready')) return;

  const video = getInlineVideoForToggle(root);
  if (!(video instanceof HTMLVideoElement)) return;

  const isNativeButton = root instanceof HTMLButtonElement;

  markVideoFallbackImagesDecorative(video);

  root.setAttribute('data-fit-inline-video-ready', '');
  if (isNativeButton) {
    root.removeAttribute('role');
    root.removeAttribute('tabindex');
    root.removeAttribute('aria-keyshortcuts');
  } else {
    root.setAttribute('tabindex', '0');
    root.setAttribute('role', 'button');
    root.setAttribute('aria-keyshortcuts', 'Enter Space');
  }
  video.removeAttribute('tabindex');
  video.removeAttribute('role');
  video.removeAttribute('aria-keyshortcuts');
  video.removeAttribute('aria-pressed');
  video.removeAttribute('aria-label');
  video.setAttribute('controlsList', 'nodownload noplaybackrate noremoteplayback');

  root.addEventListener('click', () => toggleInlineVideo(video));

  if (!isNativeButton) {
    root.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      toggleInlineVideo(video);
    });
  }

  video.addEventListener('play', () => updateInlineVideoState(video, root));
  video.addEventListener('pause', () => updateInlineVideoState(video, root));
  video.addEventListener('ended', () => updateInlineVideoState(video, root));

  updateInlineVideoState(video, root);
};

function initInlineVideoToggles(scope = document) {
  if (!(scope instanceof Element || scope instanceof Document || scope instanceof DocumentFragment)) return;
  scope.querySelectorAll(INLINE_VIDEO_ROOT_SELECTOR).forEach(initInlineVideoToggle);
}

window.FitInlineVideoToggles = window.FitInlineVideoToggles || {};
window.FitInlineVideoToggles.initAll = initInlineVideoToggles;

export function pauseAllMediaWithin(root) {
  pauseInactiveMediaWithin(root);
}

export function pauseInactiveMediaWithin(root, activeRoot = null) {
  if (!(root instanceof Element || root instanceof DocumentFragment)) return;

  root.querySelectorAll('lazy-media, model-viewer-3d, video, iframe[data-video-type], model-viewer').forEach((node) => {
    if (isElementWithinActiveRoot(node, activeRoot)) return;
    pauseMediaNode(node);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initInlineVideoToggles(), { once: true });
} else {
  initInlineVideoToggles();
}

document.addEventListener('shopify:section:load', (event) => {
  initInlineVideoToggles(event.target instanceof Element ? event.target : document);
});

document.addEventListener('fit:inline-video:init', (event) => {
  const target = event.detail?.root;
  initInlineVideoToggles(target instanceof Element || target instanceof DocumentFragment ? target : document);
});

class DeferredMedia extends FCElement {
  isPlaying = false;
  #ac = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#ac;

    document.addEventListener(FCEvents.mediaPlay, this.pauseMedia.bind(this), { signal });

    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });

    this.initializeLoadedMedia(this.querySelector('video, iframe[data-video-type]'));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#ac.abort();
  }

  updatePlayPauseHint(playing) {
    const btn = this.refs.toggleMediaButton;
    if (!(btn instanceof HTMLElement)) return;

    btn.classList.remove('hidden');
    btn.querySelector('.icon-play')?.classList.toggle('hidden', playing);
    btn.querySelector('.icon-pause')?.classList.toggle('hidden', !playing);
    btn.setAttribute('aria-pressed', String(playing));
    btn.setAttribute('aria-label', playing ? btn.dataset.pauseLabel || '' : btn.dataset.playLabel || '');
  }

  syncLoadedMediaState(mediaNode = this.querySelector('video, iframe[data-video-type]')) {
    if (mediaNode instanceof HTMLVideoElement) {
      this.isPlaying = !mediaNode.paused && !mediaNode.ended;
    } else if (mediaNode instanceof HTMLIFrameElement) {
      this.isPlaying = /[?&]autoplay=(?:1|true)\b/i.test(mediaNode.src);
    } else {
      return;
    }

    this.updatePlayPauseHint(this.isPlaying);
  }

  initializeLoadedMedia(mediaNode) {
    if (!(mediaNode instanceof HTMLVideoElement || mediaNode instanceof HTMLIFrameElement)) return;

    this.setAttribute('data-media-loaded', 'true');

    if (mediaNode instanceof HTMLVideoElement) {
      markVideoFallbackImagesDecorative(mediaNode);
      const { signal } = this.#ac;
      mediaNode.addEventListener('play', this.handleNativeMediaPlayPause, { signal });
      mediaNode.addEventListener('pause', this.handleNativeMediaPlayPause, { signal });
      mediaNode.addEventListener('ended', this.handleNativeMediaPlayPause, { signal });
    }

    requestAnimationFrame(() => this.syncLoadedMediaState(mediaNode));
  }

  handleNativeMediaPlayPause = (event) => {
    this.syncLoadedMediaState(event.currentTarget);
  };

  showDeferredMedia = () => {
    this.loadContent(true);
    // Unmute native video after user-initiated play (browser allows audio after gesture)
    const video = this.querySelector('video');
    if (video) video.muted = false;
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  };

  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;

    this.dispatchEvent(new MediaPlayEvent(this));

    const content = this.querySelector('template')?.content?.firstElementChild?.cloneNode(true);
    if (!content) return;

    this.setAttribute('data-media-loaded', 'true');
    this.appendChild(content);

    this.initializeLoadedMedia(content);

    if (focus && content instanceof HTMLElement) content.focus();

    this.refs.deferredMediaPlayButton?.classList.add('lazy-media__playing');

    // Autoplay video if attribute is set; swallow blocked-play rejections
    if (content instanceof HTMLVideoElement && content.getAttribute('autoplay') !== null) {
      content.play()?.catch(() => {});
    }
  }

  toggleMedia() {
    this.isPlaying ? this.pauseMedia() : this.playMedia();
  }

  playMedia() {
    // Dispatch play event to pause all other active media (mutually exclusive playback)
    this.dispatchEvent(new MediaPlayEvent(this));

    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      postIframeCmd(iframe, 'play');
    } else {
      this.querySelector('video')
        ?.play()
        ?.catch(() => {});
    }

    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  }

  pauseMedia() {
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      postIframeCmd(iframe, 'pause');
    } else {
      this.querySelector('video')?.pause();
    }

    this.isPlaying = false;
    if (this.getAttribute('data-media-loaded')) this.updatePlayPauseHint(this.isPlaying);
  }
}

if (!customElements.get('lazy-media')) {
  customElements.define('lazy-media', DeferredMedia);
}

class ProductModel extends DeferredMedia {
  #ac = new AbortController();

  loadContent() {
    super.loadContent();
    Shopify.loadFeatures([{ name: 'model-viewer-ui', version: '1.0', onLoad: this.setupModelViewerUI.bind(this) }]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#ac.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  async setupModelViewerUI(errs) {
    if (errs?.length) return;

    // Safari sometimes needs a moment for ModelViewerUI to appear
    if (!Shopify.ModelViewerUI) await this.#waitForModelViewerUI();
    if (!Shopify.ModelViewerUI) return;

    const el = this.querySelector('model-viewer');
    if (!el) return;

    const { signal } = this.#ac;
    this.modelViewerUI = new Shopify.ModelViewerUI(el);
    this.playMedia();

    // Track pointer origin to distinguish taps from drags
    let ptrX = 0,
      ptrY = 0;

    el.addEventListener(
      'pointerdown',
      (e) => {
        ptrX = e.clientX;
        ptrY = e.clientY;
      },
      { signal },
    );

    el.addEventListener(
      'click',
      (e) => {
        const dx = e.clientX - ptrX,
          dy = e.clientY - ptrY;
        // Only treat as tap if pointer moved less than 10px
        if (Math.sqrt(dx * dx + dy * dy) < 10) this.pauseMedia();
      },
      { signal },
    );
  }

  async #waitForModelViewerUI() {
    for (let i = 0; i < 10; i++) {
      if (Shopify.ModelViewerUI) return;
      await new Promise((r) => setTimeout(r, 50));
    }
  }
}

if (!customElements.get('model-viewer-3d')) {
  customElements.define('model-viewer-3d', ProductModel);
}
